import { IconMenu2 } from '@tabler/icons-react'
import Logo from 'assets/branding.png'
import { ActionIcon, Box, Group, Image, Skeleton } from 'components'
import {
  rem,
  useArcManagerDashboardEnabled,
  useFeatureToggle,
  useMantineTheme,
  useMediaQuery,
  useSpecificFeature,
} from 'hooks'
import { ArcLogo } from 'components/ArcLogo'
import { ISidebarMenuItem } from 'interfaces'
import { useMemo, useState } from 'react'
// Deep import on purpose: the module barrel (pages/StudentTasks) re-exports the
// page, which imports Layout → SidebarMenu and would create an import cycle.
import { useStudentTasksCount } from 'pages/StudentTasks/hooks/useStudentTasksCount'
import { SidebarHelpItem } from './components/SidebarHelpItem'
import { ArcSidebarMenuItem } from './components/ArcSidebarMenuItem'
import { SidebarMenuItem } from './components/SidebarMenuItem'
import { SidebarMenuProps } from './interfaces'
import { getArcMenu } from './menus/arcMenus'
import { getStudentMenu } from './menus/getStudentMenu'
import { guardianMenu } from './menus/guardianMenu'
import { managerMenu } from './menus/managerMenu'
import { teacherMenu } from './menus/teacherMenu'
import { useStyles } from './styles'

const ARC_SIDEBAR_LOGO_HEIGHT_PX = 28
// Arc loading state: skeleton rows with the exact dimensions of the final
// nav items (44px pill/square) so the menu doesn't jump when it resolves
const ARC_MENU_SKELETON_ROWS = 5
const ARC_MENU_ITEM_SIZE = 44
const ARC_MENU_ITEM_RADIUS = 8

export const SidebarMenu = ({ pathname, profile, userID, collapsed: collapsedProp }: SidebarMenuProps) => {
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const [internalCollapsed, setInternalCollapsed] = useState(true)
  const isArcProjectsEnabled = useFeatureToggle('arc-mosaico-project') as boolean
  // Under Arc the toggle lives in the ArcTopBar burger, so the shell/Layout
  // owns the collapsed state and passes it down; legacy keeps internal state.
  const collapsed = collapsedProp ?? internalCollapsed
  const { classes } = useStyles({ collapsed, isMobile, arcTopBar: isArcProjectsEnabled })
  // Branding follows the global Flipper flag — the same source the auth screens
  // use — so the logo can't disagree with the login branding (AR-792)
  const { enabled: isArcBrandingEnabled, loading: isArcBrandingLoading } =
    useSpecificFeature('arc-mosaico-project')
  const isManagerDashboardEnabled = useArcManagerDashboardEnabled()
  const pendingTasksCount = useStudentTasksCount(isArcProjectsEnabled && profile === 'student')

  const studentMenu = useMemo(() => getStudentMenu(userID), [userID])

  const arcMenuItems = useMemo(
    () => (isArcProjectsEnabled ? getArcMenu(profile, userID, isManagerDashboardEnabled) : []),
    [isArcProjectsEnabled, profile, userID, isManagerDashboardEnabled],
  )

  const legacyMenuItems = useMemo((): ISidebarMenuItem[] => {
    if (isArcProjectsEnabled) return []
    switch (profile) {
      case 'student':
        return studentMenu
      case 'tutor':
        return teacherMenu
      case 'manager':
        return managerMenu
      case 'guardian':
        return guardianMenu
      default:
        return []
    }
  }, [profile, isArcProjectsEnabled, studentMenu])

  return (
    <div className={classes.sidebar}>
      {!isArcProjectsEnabled && (
        <Group spacing={0} className={classes.toggleLogoBox}>
          <Box className={classes.toggleButtonBox}>
            <ActionIcon
              variant='transparent'
              color='dark'
              size='lg'
              onClick={() => setInternalCollapsed(!collapsed)}
              className={classes.actionIconToggle}
            >
              <IconMenu2 className={classes.iconToggle} />
            </ActionIcon>
          </Box>
          {!collapsed &&
            !isArcBrandingLoading &&
            (isArcBrandingEnabled ? (
              <ArcLogo height={ARC_SIDEBAR_LOGO_HEIGHT_PX} />
            ) : (
              <Image src={Logo} alt={'Mosaico'} width={rem(140)} />
            ))}
        </Group>
      )}
      <div className={classes.menuItems}>
        {isArcProjectsEnabled ? (
          <div className={classes.arcNavList}>
            {profile
              ? arcMenuItems.map(item => (
                <ArcSidebarMenuItem
                  key={item.key}
                  item={item}
                  pathname={pathname}
                  collapsed={collapsed}
                  badge={item.key === 'my_tasks' ? pendingTasksCount : undefined}
                />
              ))
              : Array.from({ length: ARC_MENU_SKELETON_ROWS }, (_, index) => (
                <Skeleton
                  key={index}
                  height={ARC_MENU_ITEM_SIZE}
                  width={collapsed ? ARC_MENU_ITEM_SIZE : undefined}
                  radius={ARC_MENU_ITEM_RADIUS}
                  mx={collapsed ? 'auto' : 0}
                />
              ))}
          </div>
        ) : (
          <ul className={classes.listRoot}>
            {legacyMenuItems.map((item: ISidebarMenuItem) => (
              <SidebarMenuItem key={item.link} item={item} pathname={pathname} collapsed={collapsed} />
            ))}
          </ul>
        )}
        {!isArcProjectsEnabled && <SidebarHelpItem collapsed={collapsed} />}
      </div>
    </div>
  )
}
